import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import Button from '../../../../components/Button';
import Input from '../../../../components/Input';
import { VisitDocument, PendingUploadFile } from '../../../../interfaces/property-visit.interface';

interface DocumentsTabProps {
  reportUrl: string;
  reportUrlError?: string;
  onReportUrlChange: (value: string) => void;
  existingDocs: VisitDocument[];
  onRemoveExistingDoc: (docId: string) => void;
  pendingPdfFile: File | null;
  pendingImageFiles: PendingUploadFile[];
  onAddPdfFile: (file: File) => void;
  onRemovePendingPdf: () => void;
  onAddImageFiles: (files: FileList) => void;
  onRemovePendingImage: (localId: string) => void;
  isEditable: boolean;
  loading: boolean;
  onSave: () => void;
  onCancel: () => void;
}

const getFileNameFromPath = (path: string) => path.split('/').pop() ?? path;

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  reportUrl, reportUrlError, onReportUrlChange,
  existingDocs, onRemoveExistingDoc,
  pendingPdfFile, pendingImageFiles,
  onAddPdfFile, onRemovePendingPdf,
  onAddImageFiles, onRemovePendingImage,
  isEditable, loading, onSave, onCancel,
}) => {
  const { t } = useTranslation();
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const existingPdfDoc = existingDocs.find((d) => d.document_type === 'pdf') ?? null;
  const existingImageDocs = existingDocs.filter((d) => d.document_type === 'image');

  const allImages: { key: string; preview: string | null; label: string; onRemove?: () => void }[] = [
    ...existingImageDocs.map((d) => ({
      key: `existing-${d.id}`,
      preview: d.file_url || null,
      label: getFileNameFromPath(d.path),
      onRemove: isEditable ? () => onRemoveExistingDoc(d.id) : undefined,
    })),
    ...pendingImageFiles.map((f) => ({
      key: `pending-${f.localId}`,
      preview: f.preview,
      label: f.file.name,
      onRemove: isEditable ? () => onRemovePendingImage(f.localId) : undefined,
    })),
  ];

  const handlePdfDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type === 'application/pdf') onAddPdfFile(file);
  };

  const handleImageDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files?.length) onAddImageFiles(files);
  };

  return (
    <>
      <div className="card-body">
        {/* Section 1: Inspection Documents */}
        <div className="row g-4">
          <div className="col-12">
            <h6 className="mb-0">1. {t('propertyVisits.documents.sectionTitle')}</h6>
          </div>

          {/* URL column */}
          <div className="col-md-6">
            <p className="fw-bold small mb-4">{t('propertyVisits.documents.reportUrl.label')}</p>
            <div className="d-flex align-items-center gap-2">
              <div className="form-floating form-floating-outline flex-fill">
                <Input
                  type="url"
                  id="reportUrl"
                  name="reportUrl"
                  label={t('propertyVisits.documents.reportUrl.label')}
                  placeholder={t('propertyVisits.documents.reportUrl.placeholder')}
                  value={reportUrl}
                  disabled={!isEditable}
                  error={!!reportUrlError}
                  errorMessage={reportUrlError}
                  onChange={(e: any) => onReportUrlChange(e.target.value)}
                />
              </div>
              <Tooltip title={reportUrl.trim() && !reportUrlError ? reportUrl : ''} arrow classes={{ tooltip: 'tooltip' }}>
                <span>
                  <Button
                    className="btn btn-outline-secondary"
                    icon={<i className="ri-external-link-line pt-1 pb-1"></i>}
                    disabled={!reportUrl.trim() || !!reportUrlError}
                    onClick={() => reportUrl.trim() && !reportUrlError && window.open(reportUrl.trim(), '_blank', 'noopener,noreferrer')}
                  />
                </span>
              </Tooltip>
            </div>
          </div>

          {/* PDF column */}
          <div className="col-md-6">
            <p className="fw-bold small mb-4">{t('propertyVisits.documents.reportPdf.label')}</p>
            {existingPdfDoc ? (
              <div className="d-flex align-items-center gap-2 p-3 border rounded bg-light">
                <i className="ri-file-pdf-line text-danger fs-5"></i>
                <span className="flex-fill text-truncate small">{getFileNameFromPath(existingPdfDoc.path)}</span>
                {existingPdfDoc.file_url && (
                  <Button
                    className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                    icon={<i className="ri-download-line ri-20px"></i>}
                    onClick={() => window.open(existingPdfDoc.file_url, '_blank', 'noopener,noreferrer')}
                  />
                )}
                {isEditable && (
                  <Button
                    className="btn btn-sm btn-icon btn-text-danger waves-effect waves-light rounded-pill"
                    icon={<i className="ri-delete-bin-line ri-20px"></i>}
                    onClick={() => onRemoveExistingDoc(existingPdfDoc.id)}
                  />
                )}
              </div>
            ) : pendingPdfFile ? (
              <div className="d-flex align-items-center gap-2 p-3 border rounded bg-light">
                <i className="ri-file-pdf-line text-danger fs-5"></i>
                <span className="flex-fill text-truncate small">{pendingPdfFile.name}</span>
                {isEditable && (
                  <Button
                    className="btn btn-sm btn-icon btn-text-danger waves-effect waves-light rounded-pill"
                    icon={<i className="ri-close-line ri-20px"></i>}
                    onClick={onRemovePendingPdf}
                  />
                )}
              </div>
            ) : (
              <div
                className="border border-dashed rounded text-center d-flex flex-column align-items-center justify-content-center"
                style={{ borderColor: '#ccc', cursor: isEditable ? 'pointer' : 'default', height: 58, padding: '0 16px' }}
                onClick={() => isEditable && pdfInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={isEditable ? handlePdfDrop : undefined}
              >
                <i className="ri-upload-cloud-2-line text-muted me-2"></i>
                <span className="text-muted small">{t('propertyVisits.documents.reportPdf.uploadPrompt')}</span>
              </div>
            )}
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf"
              className="d-none"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onAddPdfFile(f); e.target.value = ''; }}
            />
          </div>
        </div>

        <hr className="mt-6 mb-0 mx-n4" />

        {/* Section 2: Visit Images */}
        <div className="row g-6 mb-0 mt-6">
          <h6 className="mb-0">
            2. {t('propertyVisits.documents.visitImages.label')}
          </h6>

          <div className="col-12">
            {allImages.length === 0 ? (
              <div
                className="border border-dashed rounded p-4 text-center"
                style={{ borderColor: '#ccc', cursor: isEditable ? 'pointer' : 'default' }}
                onClick={() => isEditable && imageInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={isEditable ? handleImageDrop : undefined}
              >
                <i className="ri-image-add-line fs-3 text-muted d-block mb-1"></i>
                <span className="text-muted small">{t('propertyVisits.documents.visitImages.uploadPrompt')}</span>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {allImages.map((img, i) => (
                  <ImageTile
                    key={img.key}
                    preview={img.preview}
                    label={img.label}
                    index={i}
                    total={allImages.length}
                    onOpen={img.preview ? () => setLightboxIndex(i) : undefined}
                    onRemove={img.onRemove}
                  />
                ))}
                {isEditable && (
                  <div
                    onClick={() => imageInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleImageDrop}
                    style={{
                      aspectRatio: '4/3',
                      border: '2px dashed #ccc',
                      borderRadius: 6,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: '#999',
                      fontSize: 13,
                      gap: 4,
                    }}
                  >
                    <i className="ri-add-line" style={{ fontSize: 22 }}></i>
                    <span>{t('propertyVisits.documents.visitImages.addMore')}</span>
                  </div>
                )}
              </div>
            )}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              className="d-none"
              onChange={(e) => { const files = e.target.files; if (files?.length) onAddImageFiles(files); e.target.value = ''; }}
            />
          </div>
        </div>

        {lightboxIndex !== null && (
          <SimpleLightbox
            images={allImages.filter((img) => img.preview).map((img) => ({ preview: img.preview!, label: img.label }))}
            currentIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onNavigate={setLightboxIndex}
          />
        )}
      </div>

      {isEditable && (
        <>
          <hr className="m-0" />
          <div className="p-4">
            <Button
              className="btn btn-primary me-3"
              text={t('propertyVisits.addEditView.pageTexts.form.saveDocumentsBtn')}
              onClick={onSave}
              loading={loading}
            />
            <Button
              className="btn btn-outline-secondary"
              text={t('propertyVisits.addEditView.pageTexts.form.cancelBtn')}
              onClick={onCancel}
              disabled={loading}
            />
          </div>
        </>
      )}
    </>
  );
};

interface ImageTileProps {
  preview: string | null;
  label: string;
  index: number;
  total: number;
  onOpen?: () => void;
  onRemove?: () => void;
}

const ImageTile: React.FC<ImageTileProps> = ({ preview, label, index, total, onOpen, onRemove }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ position: 'relative', aspectRatio: '4/3', borderRadius: 6, overflow: 'hidden', cursor: onOpen ? 'pointer' : 'default', background: '#f0f0f0' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onOpen}
    >
      {preview ? (
        <img
          src={preview}
          alt={label}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, color: '#888' }}>
          <i className="ri-image-line" style={{ fontSize: 28 }}></i>
          <span style={{ fontSize: 11, textAlign: 'center', padding: '0 8px', wordBreak: 'break-all' }}>{label}</span>
        </div>
      )}
      {hovered && onOpen && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="ri-eye-line" style={{ color: '#fff', fontSize: 22 }}></i>
        </div>
      )}
      <span style={{ position: 'absolute', top: 4, left: 4, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11, padding: '1px 5px', borderRadius: 3, pointerEvents: 'none' }}>
        {index + 1}/{total}
      </span>
      {onRemove && (
        <div style={{ position: 'absolute', top: 8, right: 8 }} onClick={(e) => e.stopPropagation()}>
          <Button
            className="btn btn-sm position-absolute end-10 top-10 rounded-circle bg-lightest p-2"
            icon={<i className="ri-delete-bin-6-line ri-20px text-danger"></i>}
            onClick={onRemove}
          />
        </div>
      )}
    </div>
  );
};

interface SimpleLightboxProps {
  images: { preview: string; label: string }[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

const SimpleLightbox: React.FC<SimpleLightboxProps> = ({ images, currentIndex, onClose, onNavigate }) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate(currentIndex - 1);
      if (e.key === 'ArrowRight' && currentIndex < images.length - 1) onNavigate(currentIndex + 1);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [currentIndex, images.length, onClose, onNavigate]);

  if (!images.length) return null;
  const img = images[currentIndex];
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
        <img src={img.preview} alt={img.label} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 4 }} />
        <span style={{ position: 'absolute', top: -36, right: 0 }}>
          <Button
            className="btn btn-sm btn-icon btn-text-danger"
            icon={<i className="ri-close-line" style={{ color: '#fff', fontSize: 20 }}></i>}
            onClick={onClose}
          />
        </span>
        {images.length > 1 && (
          <>
            <span style={{ position: 'absolute', left: -48, top: '50%', transform: 'translateY(-50%)' }}>
              <Button
                className="btn btn-sm btn-icon"
                icon={<i className="ri-arrow-left-s-line" style={{ color: '#fff', fontSize: 24 }}></i>}
                disabled={currentIndex === 0}
                onClick={() => onNavigate(currentIndex - 1)}
              />
            </span>
            <span style={{ position: 'absolute', right: -48, top: '50%', transform: 'translateY(-50%)' }}>
              <Button
                className="btn btn-sm btn-icon"
                icon={<i className="ri-arrow-right-s-line" style={{ color: '#fff', fontSize: 24 }}></i>}
                disabled={currentIndex === images.length - 1}
                onClick={() => onNavigate(currentIndex + 1)}
              />
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentsTab;
